import {
  createContext,
  useContext,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";

/**
 * The one bar a page owns: its title, its count and filters, and its main action sit in
 * the shell's top bar rather than in a second band below it. The shell renders the slot;
 * `PageHeader` renders into it. Outside a shell (a test, an isolated surface) there is no
 * slot and the header stays where it was written.
 *
 * The shell also hands the bar its trail — the place and any page this one sits beneath —
 * which `PageHeader` sets before its title, so every page says where it is and how to go back.
 */
export type PageBarCrumb = {
  readonly label: string;
  /** The router's link to the crumb's page, without children; absent when it opens nowhere. */
  readonly link?: ReactElement<{ className?: string; children?: ReactNode }>;
};

type PageBar = { readonly slot: HTMLElement | null; readonly trail: readonly PageBarCrumb[] };

const PageBarContext = createContext<PageBar>({ slot: null, trail: [] });

export function PageBarProvider({
  trail = [],
  children,
}: {
  readonly trail?: readonly PageBarCrumb[];
  readonly children: (slot: (node: HTMLElement | null) => void) => ReactNode;
}) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  return (
    <PageBarContext.Provider value={{ slot, trail }}>
      {children(setSlot)}
    </PageBarContext.Provider>
  );
}

export function usePageBarSlot(): HTMLElement | null {
  return useContext(PageBarContext).slot;
}

export function usePageBarTrail(): readonly PageBarCrumb[] {
  return useContext(PageBarContext).trail;
}
